import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, BookOpen, Check, Bookmark, Menu, Maximize2, Sparkles, Pencil, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { YouTubeSection, stripYouTubeUrls } from "./YouTubeEmbed";
import { MarkdownNotes } from "./MarkdownNotes";

/**
 * CourseReader - immersive full-screen reader for sub-topics & points.
 * Replaces the cramped expandable tree experience with a Kindle/Medium-style
 * long-form reader.
 *
 * Props:
 *   - organ: the active organ node (full tree)
 *   - organTitle: display title
 *   - startPointId: which leaf/point to open initially
 *   - readPoints: {[id]: true} map
 *   - bookmarkedIds: {[id]: true} map
 *   - personalNotes: {[id]: text}
 *   - onClose: () => void
 *   - onToggleRead, onToggleBookmark, onSaveNote
 *   - lang
 */
export const CourseReader = ({
  organ,
  organTitle,
  startPointId,
  readPoints = {},
  bookmarkedIds = {},
  personalNotes = {},
  onClose,
  onToggleRead,
  onToggleBookmark,
  onSaveNote,
  lang = "ms",
  apiUrl,
  isAdmin = false,
}) => {
  const isEn = lang === "en";

  // Flatten all leaf nodes (points without children) in reading order
  const flatPoints = useMemo(() => {
    const out = [];
    const walk = (nodes, breadcrumb) => {
      for (const n of nodes || []) {
        const hasChildren = (n.children || []).length > 0;
        if (!hasChildren) {
          out.push({ ...n, _breadcrumb: breadcrumb });
        } else {
          walk(n.children, [...breadcrumb, n.title]);
        }
      }
    };
    walk(organ?.children || [], []);
    return out;
  }, [organ]);

  // Outline = top-level (Module / Chapter) items for sidebar
  const outline = useMemo(() => {
    return (organ?.children || []).map((c) => ({ id: c.id, title: c.title, count: countLeaves(c) }));
  }, [organ]);

  const initialIndex = useMemo(() => {
    if (!startPointId) return 0;
    const idx = flatPoints.findIndex((p) => p.id === startPointId);
    return idx >= 0 ? idx : 0;
  }, [flatPoints, startPointId]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const scrollerRef = useRef(null);

  // Official content notes are NOT in the slim syllabus tree - fetch them per point.
  const [notesById, setNotesById] = useState({});
  const [loadingContent, setLoadingContent] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState("");

  const current = flatPoints[currentIndex];

  // Lazy-load official notes (with server-side gating) whenever the point changes
  useEffect(() => {
    const id = current?.id;
    if (!id || !apiUrl || notesById[id] !== undefined) return;
    let cancelled = false;
    (async () => {
      setLoadingContent(true);
      try {
        const res = await axios.get(`${apiUrl}/nodes/${id}/content`);
        const map = res.data?.notes || {};
        if (!cancelled) setNotesById((prev) => ({ ...prev, ...map, [id]: map[id] ?? "" }));
      } catch (e) {
        if (!cancelled) setNotesById((prev) => ({ ...prev, [id]: "" }));
      } finally {
        if (!cancelled) setLoadingContent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [current, apiUrl, notesById]);

  const handleGenerateAI = useCallback(async () => {
    if (!current) return;
    setAiBusy(true);
    try {
      const res = await axios.post(`${apiUrl}/admin/nodes/${current.id}/generate-notes?save=true`);
      setNotesById((prev) => ({ ...prev, [current.id]: res.data.notes || "" }));
      setEditing(false);
      toast.success(isEn ? "AI notes generated & saved" : "Nota AI dijana & disimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEn ? "Failed to generate notes" : "Gagal menjana nota"));
    } finally {
      setAiBusy(false);
    }
  }, [current, apiUrl, isEn]);

  const handleSaveEdit = useCallback(async () => {
    if (!current) return;
    setAiBusy(true);
    try {
      await axios.put(`${apiUrl}/nodes/${current.id}`, { notes: editDraft });
      setNotesById((prev) => ({ ...prev, [current.id]: editDraft }));
      setEditing(false);
      toast.success(isEn ? "Notes saved" : "Nota disimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEn ? "Failed to save" : "Gagal menyimpan"));
    } finally {
      setAiBusy(false);
    }
  }, [current, apiUrl, editDraft, isEn]);

  const isRead = !!(current && readPoints[current.id]);
  const isBookmarked = !!(current && bookmarkedIds[current.id]);
  const totalPoints = flatPoints.length;
  const readCount = flatPoints.filter((p) => readPoints[p.id]).length;
  const overallPct = totalPoints > 0 ? Math.round((readCount / totalPoints) * 100) : 0;

  // Reset note draft + scroll to top on point change
  useEffect(() => {
    if (current) {
      setNoteDraft(personalNotes[current.id] || "");
    }
    setShowNotes(false);
    if (scrollerRef.current) scrollerRef.current.scrollTo({ top: 0, behavior: "auto" });
  }, [currentIndex, current, personalNotes]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);
  const goNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(flatPoints.length - 1, i + 1));
  }, [flatPoints.length]);

  const handleMarkRead = useCallback(() => {
    if (current && onToggleRead) onToggleRead(current.id);
  }, [current, onToggleRead]);

  const handleMarkReadThenNext = useCallback(() => {
    if (current && onToggleRead && !isRead) onToggleRead(current.id);
    if (currentIndex < flatPoints.length - 1) {
      setTimeout(() => goNext(), 150);
    }
  }, [current, onToggleRead, isRead, currentIndex, flatPoints.length, goNext]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.target?.tagName === "TEXTAREA" || e.target?.tagName === "INPUT") return;
      if (e.key === "ArrowRight" || e.key === "j") goNext();
      else if (e.key === "ArrowLeft" || e.key === "k") goPrev();
      else if (e.key === "Escape") onClose?.();
      else if (e.key === "m") handleMarkRead();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goNext, goPrev, onClose, handleMarkRead]);

  // Persist personal note (debounced via blur)
  const handleNoteSave = () => {
    if (!current) return;
    const original = personalNotes[current.id] || "";
    if (noteDraft !== original && onSaveNote) onSaveNote(current.id, noteDraft);
  };

  if (!current) {
    return (
      <div className="fixed inset-0 z-[90] bg-white flex items-center justify-center" data-testid="course-reader-empty">
        <div className="text-center">
          <p className="text-slate-500 text-sm mb-3">{isEn ? "No readable points in this organ." : "Tiada topik untuk dibaca dalam Organ ini."}</p>
          <button onClick={onClose} className="px-4 py-2 bg-[#0A0A0A] text-white text-sm rounded-full font-semibold">{isEn ? "Close" : "Tutup"}</button>
        </div>
      </div>
    );
  }

  const rawNotes = notesById[current.id] !== undefined ? notesById[current.id] : (current.notes || "");
  const cleanNotes = stripYouTubeUrls(rawNotes || "");
  const hasBody = !!(cleanNotes.trim() || (rawNotes || "").includes("youtube"));
  const noteKnown = notesById[current.id] !== undefined;

  return (
    <div className="fixed inset-0 z-[90] bg-white flex flex-col" data-testid="course-reader">
      {/* Top bar */}
      <div className="h-14 border-b border-slate-100 flex items-center px-3 sm:px-5 gap-2 bg-white/95 backdrop-blur-sm flex-shrink-0">
        <button
          onClick={() => setSidebarOpen((v) => !v)}
          className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          title={isEn ? "Outline" : "Senarai Kandungan"}
          data-testid="reader-outline-toggle"
        >
          <Menu size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{organTitle}</div>
          <div className="text-sm font-bold text-slate-800 truncate">{current.title}</div>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-xs text-slate-400 mr-2">
          <span data-testid="reader-progress">
            {currentIndex + 1}/{totalPoints}
          </span>
        </div>
        <button
          onClick={() => current && onToggleBookmark && onToggleBookmark(current.id)}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
            isBookmarked ? "bg-amber-50 text-amber-500" : "hover:bg-slate-100 text-slate-400"
          }`}
          title={isBookmarked ? (isEn ? "Bookmarked" : "Disimpan") : (isEn ? "Bookmark" : "Simpan")}
          data-testid="reader-bookmark"
        >
          <Bookmark size={16} fill={isBookmarked ? "currentColor" : "none"} />
        </button>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          title={isEn ? "Close (Esc)" : "Tutup (Esc)"}
          data-testid="reader-close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Reading progress bar */}
      <div className="h-1 bg-slate-100 flex-shrink-0">
        <div
          className="h-1 bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-700"
          style={{ width: `${((currentIndex + 1) / totalPoints) * 100}%` }}
        />
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Outline sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              initial={{ x: -260, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -260, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="w-64 border-r border-slate-100 bg-slate-50/60 overflow-y-auto flex-shrink-0"
            >
              <div className="p-4">
                <h3 className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">{isEn ? "Outline" : "Senarai Kandungan"}</h3>
                <ul className="space-y-1">
                  {outline.map((o) => {
                    const isActiveSection = current._breadcrumb?.[0] === o.title;
                    return (
                      <li key={o.id}>
                        <button
                          onClick={() => {
                            const idx = flatPoints.findIndex((p) => p._breadcrumb?.[0] === o.title);
                            if (idx >= 0) setCurrentIndex(idx);
                            setSidebarOpen(false);
                          }}
                          className={`w-full text-left px-2.5 py-2 rounded-lg text-xs font-medium transition ${
                            isActiveSection ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:bg-white hover:text-slate-700"
                          }`}
                        >
                          <span className="line-clamp-2">{o.title}</span>
                          <span className="text-[10px] text-slate-300 mt-0.5 block">{o.count} {isEn ? "items" : "item"}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Reading area */}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 sm:py-10">
          <article className="max-w-[720px] mx-auto" data-testid="reader-article">
            {/* Breadcrumb */}
            {current._breadcrumb && current._breadcrumb.length > 0 && (
              <div className="text-[11px] sm:text-xs text-slate-400 mb-3 flex items-center gap-1 flex-wrap">
                {current._breadcrumb.map((b, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {i > 0 && <ChevronRight size={11} className="text-slate-300" />}
                    <span>{b}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight mb-6" style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
              {current.title}
            </h1>

            {/* Body content */}
            {loadingContent && !noteKnown ? (
              <div className="py-16 flex items-center justify-center text-slate-400" data-testid="reader-loading-content">
                <Loader2 size={22} className="animate-spin" />
              </div>
            ) : !hasBody ? (
              <div className="py-10 px-4 rounded-2xl bg-slate-50 border border-dashed border-slate-200 text-center" data-testid="reader-empty-notes">
                <BookOpen size={28} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  {isEn ? "No detailed notes yet for this point - mark as read to continue." : "Tiada nota terperinci untuk topik ini - tandakan sudah baca untuk teruskan."}
                </p>
                {isAdmin && !editing && (
                  <button
                    onClick={handleGenerateAI}
                    disabled={aiBusy}
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-semibold hover:bg-[#1d4ed8] disabled:opacity-60 transition-colors shadow-sm"
                    data-testid="reader-generate-ai"
                  >
                    {aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {aiBusy ? (isEn ? "Generating…" : "Menjana…") : (isEn ? "Generate from Slides (AI)" : "Jana Nota ikut Slide (AI)")}
                  </button>
                )}
              </div>
            ) : (
              <div className="prose prose-slate max-w-none">
                <YouTubeSection notes={rawNotes || ""} lang={lang} />
                {cleanNotes.trim() && <MarkdownNotes text={cleanNotes} />}
              </div>
            )}

            {/* Admin authoring toolbar */}
            {isAdmin && (
              <div className="mt-6 pt-4 border-t border-dashed border-slate-200" data-testid="reader-admin-tools">
                {editing ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#2563EB]">
                      <Pencil size={12} /> {isEn ? "Edit content notes" : "Sunting nota kandungan"}
                    </div>
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      placeholder={isEn ? "Write content (Markdown supported)…" : "Tulis kandungan (Markdown disokong)…"}
                      className="w-full min-h-[220px] p-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y leading-relaxed font-mono"
                      data-testid="reader-edit-textarea"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button onClick={handleSaveEdit} disabled={aiBusy} className="px-4 py-2 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#1d4ed8] disabled:opacity-60 flex items-center gap-1.5" data-testid="reader-save-edit">
                        {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {isEn ? "Save" : "Simpan"}
                      </button>
                      <button onClick={handleGenerateAI} disabled={aiBusy} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60 flex items-center gap-1.5">
                        <Sparkles size={13} /> {isEn ? "Regenerate (AI)" : "Jana Semula (AI)"}
                      </button>
                      <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-full text-slate-500 text-xs font-semibold hover:bg-slate-100">
                        {isEn ? "Cancel" : "Batal"}
                      </button>
                    </div>
                  </div>
                ) : (
                  hasBody && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => { setEditDraft(rawNotes || ""); setEditing(true); }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors"
                        data-testid="reader-edit-notes"
                      >
                        <Pencil size={13} /> {isEn ? "Edit notes" : "Sunting Nota"}
                      </button>
                      <button
                        onClick={handleGenerateAI}
                        disabled={aiBusy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60 transition-colors"
                        data-testid="reader-regenerate-ai"
                      >
                        {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {isEn ? "Regenerate (AI)" : "Jana Semula (AI)"}
                      </button>
                    </div>
                  )
                )}
              </div>
            )}

            {/* Personal note */}
            <div className="mt-10 pt-6 border-t border-slate-100">
              <button
                onClick={() => setShowNotes((v) => !v)}
                className="text-xs font-semibold text-slate-500 hover:text-slate-700 mb-2 flex items-center gap-1.5"
                data-testid="reader-toggle-notes"
              >
                <ChevronRight size={12} className={`transition-transform ${showNotes ? "rotate-90" : ""}`} />
                {isEn ? "Personal note" : "Nota peribadi"}
                {(personalNotes[current.id] || "").trim() && <span className="text-[10px] text-emerald-500">●</span>}
              </button>
              {showNotes && (
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onBlur={handleNoteSave}
                  placeholder={isEn ? "Capture your thoughts…" : "Tulis nota anda di sini…"}
                  className="w-full min-h-[110px] p-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y leading-relaxed"
                  data-testid="reader-personal-note"
                />
              )}
            </div>
          </article>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="h-16 border-t border-slate-100 flex items-center px-3 sm:px-5 gap-2 bg-white flex-shrink-0">
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className="px-3 sm:px-4 py-2.5 rounded-full text-xs sm:text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          data-testid="reader-prev"
        >
          <ChevronLeft size={14} /> <span className="hidden sm:inline">{isEn ? "Previous" : "Sebelum"}</span>
        </button>

        <button
          onClick={handleMarkRead}
          className={`flex-1 sm:flex-none sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition ${
            isRead ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-[#0A0A0A] text-white hover:bg-[#1a1a1a]"
          }`}
          data-testid="reader-mark-read"
        >
          <Check size={14} /> {isRead ? (isEn ? "Read" : "Sudah baca") : (isEn ? "Mark Read" : "Tandakan")}
        </button>

        <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-400 ml-2">
          <Maximize2 size={11} /> {overallPct}%
        </div>

        <button
          onClick={handleMarkReadThenNext}
          disabled={currentIndex === flatPoints.length - 1}
          className="px-3 sm:px-4 py-2.5 rounded-full text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 ml-auto"
          data-testid="reader-next"
        >
          <span className="hidden sm:inline">{isEn ? "Next" : "Seterusnya"}</span> <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

function countLeaves(node) {
  if (!node) return 0;
  const children = node.children || [];
  if (children.length === 0) return 1;
  return children.reduce((sum, c) => sum + countLeaves(c), 0);
}

export default CourseReader;
